// KaTeX (math) loader + render helper compartilhado.
//
// Carrega KaTeX (CSS + main + auto-render extension) via CDN de forma
// idempotente. Depois, `renderMathIn(el)` varre o elemento procurando os
// delimitadores `$...$`, `$$...$$`, `\(...\)` e `\[...\]` e troca por SVG/HTML
// matemático renderizado.
//
// Inclui macros pra notação brasileira de provas (`\sen`, `\tg`, `\cotg`,
// `\cossec`, `\arcsen`, `\arctg`, `\arccotg`, `\mdc`, `\mmc`) — por default o
// KaTeX só conhece `\sin`, `\cos`, `\tan`. Sem esses aliases, prompt teria
// que gerar `\operatorname{sen}` toda vez e fidelidade visual fica frágil.
//
// Extraído de student-new/StudyWorkspace.tsx pra ser reusado em
// manager-new (preview de upload), teacher-new (revisão), etc.

const KATEX_VERSION = "0.16.11";
let katexLoadPromise: Promise<void> | null = null;

export function ensureKaTeX(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (katexLoadPromise) return katexLoadPromise;
  katexLoadPromise = new Promise<void>((resolve, reject) => {
    // CSS (idempotente)
    if (!document.querySelector('link[data-katex="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.css`;
      link.dataset.katex = "1";
      document.head.appendChild(link);
    }
    // Main script
    const main = document.createElement("script");
    main.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.js`;
    main.onload = () => {
      // mhchem extension — habilita \ce{...} (química, ex: \ce{H2O}) e \pu
      // (unidades físicas). Precisa carregar ANTES de qualquer chamada render
      // porque registra os macros no `katex.__defineMacro`.
      // [[reference-katex-mhchem]]
      const mh = document.createElement("script");
      mh.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/contrib/mhchem.min.js`;
      mh.onload = () => {
        // Auto-render extension
        const ar = document.createElement("script");
        ar.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/contrib/auto-render.min.js`;
        ar.onload = () => resolve();
        ar.onerror = () => reject(new Error("KaTeX auto-render falhou ao carregar"));
        document.head.appendChild(ar);
      };
      mh.onerror = () => {
        // mhchem é opcional — se falhar, segue sem química mas continua o resto.
        console.warn("[math] mhchem falhou ao carregar (química não vai funcionar)");
        const ar = document.createElement("script");
        ar.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/contrib/auto-render.min.js`;
        ar.onload = () => resolve();
        ar.onerror = () => reject(new Error("KaTeX auto-render falhou ao carregar"));
        document.head.appendChild(ar);
      };
      document.head.appendChild(mh);
    };
    main.onerror = () => reject(new Error("KaTeX falhou ao carregar"));
    document.head.appendChild(main);
  }).catch((err) => {
    console.warn("[math] KaTeX:", err);
    // Não bloqueia render — só não vai ter math bonita.
  });
  return katexLoadPromise;
}

export const KATEX_BR_MACROS: Record<string, string> = {
  "\\sen": "\\operatorname{sen}",
  "\\tg": "\\operatorname{tg}",
  "\\cotg": "\\operatorname{cotg}",
  "\\cossec": "\\operatorname{cossec}",
  "\\arcsen": "\\operatorname{arcsen}",
  "\\arctg": "\\operatorname{arctg}",
  "\\arccotg": "\\operatorname{arccotg}",
  "\\mdc": "\\operatorname{mdc}",
  "\\mmc": "\\operatorname{mmc}"
};

/** Pré-processa texto matemático antes do KaTeX consumir. Hoje cobre:
 *  - `\ce<chemistry>` sem chaves → `\ce{<chemistry>}` (química). Bancos
 *    legados foram extraídos sem as chaves; em vez de quebrar a renderização
 *    desses bancos a gente é forgiving aqui.
 *  - `\pu<unit>` sem chaves → `\pu{<unit>}` (unidades físicas mhchem). */
function preProcessMath(s: string): string {
  let out = s;
  // \ceHAzO3 → \ce{HAzO3}. Só captura se NÃO houver `{` logo após (senão já
  // está bem formado). Aceita letras + dígitos; para no primeiro espaço/op.
  out = out.replace(/\\ce(?!\s*\{)([A-Za-z][A-Za-z0-9]*)/g, "\\ce{$1}");
  out = out.replace(/\\pu(?!\s*\{)([A-Za-z0-9.,/]+)/g, "\\pu{$1}");
  return out;
}

export function renderMathIn(el: HTMLElement | null) {
  if (!el || typeof window === "undefined") return;
  const w = window as unknown as {
    renderMathInElement?: (el: HTMLElement, opts: unknown) => void;
  };
  if (typeof w.renderMathInElement !== "function") return;
  try {
    w.renderMathInElement(el, {
      delimiters: [
        { left: "$$", right: "$$", display: true },
        { left: "$", right: "$", display: false },
        { left: "\\(", right: "\\)", display: false },
        { left: "\\[", right: "\\]", display: true }
      ],
      macros: KATEX_BR_MACROS,
      throwOnError: false,
      // preProcess do auto-render extension — chamado pra cada match de
      // delimitador antes de KaTeX renderizar. Aceita string → string.
      preProcess: preProcessMath
    });
  } catch (err) {
    console.warn("[math] KaTeX render falhou:", err);
  }
}
